import React, { useState } from 'react';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { websiteCategories } from '../websiteTypesData';
import { ScrollReveal } from './ScrollAnimations';

const WebsiteServices = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    indigo: 'from-indigo-500 to-indigo-600',
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600',
    rose: 'from-rose-500 to-rose-600',
    red: 'from-red-500 to-red-600',
    orange: 'from-orange-500 to-orange-600',
    amber: 'from-amber-500 to-amber-600',
    yellow: 'from-yellow-500 to-yellow-600',
    lime: 'from-lime-500 to-lime-600',
    green: 'from-green-500 to-green-600',
    emerald: 'from-emerald-500 to-emerald-600',
    teal: 'from-teal-500 to-teal-600',
    cyan: 'from-cyan-500 to-cyan-600',
    sky: 'from-sky-500 to-sky-600',
    violet: 'from-violet-500 to-violet-600',
    fuchsia: 'from-fuchsia-500 to-fuchsia-600',
    slate: 'from-slate-500 to-slate-600',
    gray: 'from-gray-500 to-gray-600',
    stone: 'from-stone-500 to-stone-600'
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/10 dark:to-purple-900/10 rounded-full filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-100 to-blue-100 dark:from-cyan-900/10 dark:to-blue-900/10 rounded-full filter blur-3xl opacity-30"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Hero Section */}
        <ScrollReveal>
          <div className="text-center mb-20">
            <motion.div 
              className="inline-flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg text-blue-700 dark:text-blue-300 px-5 py-2.5 rounded-full mb-6 border border-blue-200 dark:border-blue-800 shadow-lg"
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Sparkles className="w-4 h-4 animate-pulse" />
              <span className="text-sm font-semibold">Complete Website Solutions</span>
            </motion.div>
            
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              We Build Every Type of Website
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              From idea to launch - we handle everything. No technical knowledge needed.
            </motion.p>

            {/* Process Steps */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {['Design', 'Development', 'Hosting', 'Support'].map((step, index) => (
                <motion.div 
                  key={index}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-xl p-4 shadow-lg border border-gray-200/50 dark:border-gray-700/50"
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-2"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Check className="w-5 h-5 text-white" />
                  </motion.div>
                  <div className="font-semibold text-gray-900 dark:text-white">{step}</div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div 
              className="flex flex-wrap items-center justify-center gap-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  onClick={scrollToContact}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                30+ Website Types • Ready in Weeks • Full Support
              </p>
            </motion.div>
          </div>
        </ScrollReveal>

        {/* Categories */}
        {websiteCategories.map((category, categoryIndex) => (
          <motion.div 
            key={category.id}
            className="mb-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            {/* Category Header */}
            <ScrollReveal delay={0.1}>
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  {category.title}
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {category.description}
                </p>
              </div>
            </ScrollReveal>

            {/* Website Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {category.websites.map((website, index) => {
                const IconComponent = website.icon;
                const isHovered = hoveredCard === website.id;
                
                return (
                  <motion.div
                    key={website.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <motion.div
                      whileHover={{ y: -8, scale: 1.03 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <Card
                        className="group relative overflow-hidden hover:shadow-2xl transition-all duration-500 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-2 cursor-pointer h-full"
                        onMouseEnter={() => setHoveredCard(website.id)}
                        onMouseLeave={() => setHoveredCard(null)}
                      >
                        {/* Default View */}
                        <div className={`transition-opacity duration-300 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                          <CardContent className="p-6 text-center">
                            {/* Icon with animation */}
                            <motion.div 
                              className={`w-16 h-16 bg-gradient-to-br ${colorClasses[website.color]} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}
                              whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                              transition={{ duration: 0.5 }}
                            >
                              <IconComponent className="w-8 h-8 text-white" />
                            </motion.div>
                            
                            {/* Website Name */}
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                              {website.name}
                            </h4>
                            
                            {/* Tagline */}
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {website.tagline}
                            </p>
                          </CardContent>
                        </div>

                        {/* Hover Overlay */}
                        <motion.div 
                          className={`absolute inset-0 bg-gradient-to-br ${colorClasses[website.color]} p-6 flex flex-col justify-between text-white`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: isHovered ? 1 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div>
                            <div className="flex items-center space-x-2 mb-4">
                              <IconComponent className="w-6 h-6" />
                              <h4 className="text-lg font-bold">{website.name}</h4>
                            </div>
                            
                            <div className="space-y-3 text-sm">
                              <div>
                                <Badge className="bg-white/20 text-white border-0 mb-2">
                                  What it's for
                                </Badge>
                                <p className="text-white/90 leading-relaxed">
                                  {website.whatFor}
                                </p>
                              </div>
                              
                              <div>
                                <Badge className="bg-white/20 text-white border-0 mb-2">
                                  Who should use
                                </Badge>
                                <p className="text-white/90 leading-relaxed">
                                  {website.whoUses}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="w-full mt-4 bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-lg"
                              onClick={scrollToContact}
                            >
                              Get Started
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </motion.div>
                        </motion.div>
                      </Card>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        ))}

        {/* Bottom CTA */}
        <ScrollReveal>
          <motion.div 
            className="mt-20 text-center bg-gradient-to-r from-blue-50 via-purple-50 to-cyan-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-cyan-900/20 rounded-3xl p-12 border-2 border-blue-200/50 dark:border-blue-800/50 backdrop-blur-lg shadow-2xl"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Launch Your Website?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              We handle everything from design to deployment. No technical knowledge needed – just tell us your business needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button
                  size="lg"
                  onClick={scrollToContact}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 shadow-xl"
                >
                  Start Your Project Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default WebsiteServices;
